import type {TMajorActionSlyDealData, TPlayerId} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import {SlyDealCard} from '~/models/game/Cards/ActionCard'
import {propertyCardFactory} from '~/models/game/factories'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import {unambiguousNeighborhoodDescriptionData} from '~/models/game/MajorActions/utils'
import type {AnyPropertyCard} from '~/models/game/types'
import {sentenceCase, xVerbY} from '~/utils/language'

export default class MajorActionSlyDeal extends MajorAction<TMajorActionSlyDealData> {
  card: SlyDealCard
  minorActorPlayerId: TPlayerId // target player
  requestedCard: AnyPropertyCard

  constructor(data: TMajorActionSlyDealData) {
    super(data)
    this.card = new SlyDealCard(data.card)
    this.minorActorPlayerId = data.minorActorPlayerId
    this.requestedCard = propertyCardFactory(data.requestedCard)
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const minorActor = gamePosition.players.find(
      o => o.id === this.minorActorPlayerId
    )
    if (!minorActor) {
      throw new Error(`Minor actor not found in MajorActionSlyDeal description`)
    }

    const portfolioNeighborhoodOfRequestedCard =
      minorActor.portfolio.neighborhoods.find(pn =>
        pn.properties.find(p => p.id === this.requestedCard.id)
      )

    if (!portfolioNeighborhoodOfRequestedCard) {
      throw new Error(
        `Sly Deal's requested card not found in minor actor's portfolio`
      )
    }

    const {sameColorCount, sameColorIndex} = unambiguousNeighborhoodDescriptionData(
      minorActor.portfolio,
      portfolioNeighborhoodOfRequestedCard
    )

    const dropFirstNoun = tense === 'imperativePrompt'
    const {conjugatedVerb, xNoun, yNounPossessive} = xVerbY(
      tense,
      majorActor,
      dropFirstNoun ? 'Play' : 'play',
      minorActor,
      currentPlayer?.id
    )

    const actor = dropFirstNoun
      ? undefined
      : xNoun === majorActor.username
        ? xNoun
        : sentenceCase(xNoun)
    const fromNeighborhood =
      sameColorCount > 1
        ? `from ${portfolioNeighborhoodOfRequestedCard.displayColor(skin)} ${
            skins[skin].vocab.neighborhood.singular
          } #${sameColorIndex + 1}`
        : undefined

    return skins[skin].gameLogDescriptions.majorActions.slyDeal({
      tense,
      actor,
      playVerb: conjugatedVerb,
      cardName: this.card.displayName(skin),
      targetPossessive: yNounPossessive,
      requestedCardName: this.requestedCard.displayName(skin),
      fromNeighborhood,
    })
  }
}
